import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { getDefaultStore, useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";

import { cn } from "@repo/cn";
import Check from "@repo/icons/default/Check";
import Send from "@repo/icons/default/Send";
import { CpsFeedbackBubble } from "corpus";

import { Button } from "@/components/ui/button/button";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { regulatoryDocumentAtom } from "@/lib/atoms/regulatory-document-atom";
import { isReasonStepComplete } from "@/lib/form-schemas/regulatory-document-schema";
import {
  isBindingDeclaration,
  prazoDeAssinatura,
} from "@/lib/helpers/regulatory-documents";

import type { JSX } from "react";

interface PassoProps {
  titulo: string;
  detalhe: string;
  estado: "feito" | "andamento" | "pendente";
}

const Passo = ({ titulo, detalhe, estado }: PassoProps): JSX.Element => (
  <li className="flex items-start gap-3">
    <span
      className={cn(
        "mt-0.5 size-6 shrink-0 rounded-full border-2 flex items-center justify-center",
        {
          "border-secondary-400 bg-secondary-400": estado === "feito",
          "border-secondary-200 bg-secondary-50": estado === "andamento",
          "border-neutral-200": estado === "pendente",
        },
      )}
      aria-hidden="true"
    >
      {estado === "feito" ? <Check className="fill-white" /> : null}
    </span>
    <div>
      <p
        className={cn("text-sm font-medium", {
          "text-neutral-700": estado !== "pendente",
          "text-neutral-400": estado === "pendente",
        })}
      >
        {titulo}
      </p>
      <p className="text-xs text-neutral-500">{detalhe}</p>
    </div>
  </li>
);

const listaDeUnidades = (unidades: string[]): string => {
  if (unidades.length <= 1) return unidades.join("");

  return `${unidades.slice(0, -1).join(", ")} e ${unidades.at(-1)}`;
};

const RegulatoryDocumentConfirmationPage = (): JSX.Element => {
  const draft = useAtomValue(regulatoryDocumentAtom);
  const { user } = Route.useRouteContext();
  const resetDraft = useResetAtom(regulatoryDocumentAtom);
  const router = useRouter();

  const unidadesEscolhidas = draft.selectedLocations.map((location) => location.nome);
  const ehRetirada = unidadesEscolhidas.length > 0;

  const goToDocuments = (): void => {
    router.history.go(-3);
    resetDraft();
  };
  const goToRequests = async (): Promise<void> => {
    router.history.go(-2);
    await router.navigate({ to: "/settings/regulatory-documents/requests" });
    resetDraft();
  };

  return (
    <RoundedLayout>
      <Page
        className="flex h-full flex-col justify-between gap-5 px-6 pb-0 pt-10"
        showCustomerIoContainer={false}
      >
        <div className="flex flex-col gap-5">
          <div className="flex flex-col items-center gap-2 text-center">
            <CpsFeedbackBubble Icon={Send} />
            <h1 className="text-xl font-semibold text-neutral-700">
              {ehRetirada && unidadesEscolhidas.length > 1
                ? "Solicitações enviadas"
                : "Solicitação enviada"}
            </h1>
          </div>

          <ul className="flex flex-col gap-4 rounded-xl border border-neutral-100 bg-white p-4 shadow-xs">
            <Passo
              titulo="Documento gerado"
              detalhe={`Com os dados do seu cadastro em ${draft.stateLabel || draft.siglaUF}`}
              estado="feito"
            />
            <Passo
              titulo={ehRetirada ? "Assinatura na unidade" : "Assinatura da Livance"}
              detalhe={`Em andamento (prazo de até ${prazoDeAssinatura(draft.codMotivoDocumento, draft.siglaUF)})`}
              estado="andamento"
            />
            <Passo
              titulo={ehRetirada ? "Retirada na unidade" : "Envio para o seu e-mail"}
              detalhe={
                ehRetirada
                  ? listaDeUnidades(unidadesEscolhidas)
                  : (user.email ?? "e-mail do seu cadastro")
              }
              estado="pendente"
            />
          </ul>
        </div>

        <div className="sticky bottom-0 flex w-full flex-col gap-2 bg-white pb-6 pt-4">
          <Button fullWidth onClick={() => void goToRequests()}>
            Acompanhar solicitações
          </Button>
          <Button fullWidth color="secondaryLight" onClick={goToDocuments}>
            Voltar para Documentos
          </Button>
        </div>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/settings/regulatory-documents/confirmation")({
  component: RegulatoryDocumentConfirmationPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-documentos-regulatorios-no-app"]) {
      redirect({ to: "/settings", throw: true });
    }

    const draft = getDefaultStore().get(regulatoryDocumentAtom);

    if (
      !isReasonStepComplete(draft) ||
      !isBindingDeclaration(draft.codTipoDocumentoRegulatorio)
    ) {
      redirect({ to: "/settings/regulatory-documents", throw: true });
    }
  },
});
